import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, race, timer } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { PortalConfigurationView } from '../models/PortalConfigurationView';

const CONFIG_WAIT_TIMEOUT_MS = 8000;

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private config: PortalConfigurationView = new PortalConfigurationView();
  private configSubject = new BehaviorSubject<PortalConfigurationView>(
    this.config,
  );
  private resolvedSubject = new BehaviorSubject<boolean>(false);

  constructor() {
    this.hydrateFromCache();
  }

  private hydrateFromCache(): void {
    try {
      const cached = localStorage.getItem('portalConfig');
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        this.config = { ...new PortalConfigurationView(), ...parsed };
        this.configSubject.next(this.config);
        this.resolvedSubject.next(true);
      }
    } catch {
      // Bad cache — ignore
    }
  }

  setConfig(config: PortalConfigurationView): void {
    this.config = config;
    this.configSubject.next(config);
    this.resolvedSubject.next(true);
    try {
      localStorage.setItem('portalConfig', JSON.stringify(config));
    } catch {
      // localStorage quota — ignore
    }
  }

  getConfig(): PortalConfigurationView {
    return this.config;
  }

  // Observable for reactive updates
  getConfig$(): Observable<PortalConfigurationView> {
    return this.configSubject.asObservable();
  }

  // Get config from localStorage if not available in memory
  getConfigFromStorage(): PortalConfigurationView {
    if (!this.config || Object.keys(this.config).length === 0) {
      this.hydrateFromCache();
    }
    return this.config;
  }

  isResolved(): boolean {
    return this.resolvedSubject.value;
  }

  isResolved$(): Observable<boolean> {
    return this.resolvedSubject.asObservable();
  }

  whenResolved(
    timeoutMs: number = CONFIG_WAIT_TIMEOUT_MS,
  ): Observable<PortalConfigurationView> {
    if (this.isResolved()) {
      return of(this.config);
    }

    return race(
      this.resolvedSubject.pipe(filter((resolved) => resolved)),
      timer(timeoutMs).pipe(map(() => false)),
    ).pipe(
      take(1),
      switchMap(() => of(this.config)),
    );
  }
}
